import type { ModuleName } from "../features/modules/names";

// Пункты navbar-меню и логика их видимости по составу включённых
// модулей (ADR-0083). Чистая логика без React: тестируется напрямую.

// Пункт navbar-меню. `to` — целевой роут, `labelKey` — ключ i18n,
// `modules` — модули, любой из которых открывает раздел (пусто —
// раздел ядра, виден всегда).
export interface NavItem {
  readonly to: string;
  readonly labelKey: string;
  readonly modules: readonly ModuleName[];
}

// Верхнеуровневые разделы вне группы «Настройки».
export const TOP_LEVEL: readonly NavItem[] = [
  { to: "/queue", labelKey: "nav.queue", modules: ["task"] },
];

// Подразделы «Настройки» — группирующий уровень без своей страницы.
// «Организация» — раздел ядра (`modules: []`): имя организации видно без
// прикладных модулей, секция бюджета повторов внутри страницы гейтится
// модулем settings.
export const SETTINGS_ITEMS: readonly NavItem[] = [
  {
    to: "/settings/organization",
    labelKey: "nav.settingsOrganization",
    modules: [],
  },
  {
    to: "/settings/connections",
    labelKey: "nav.settingsConnections",
    modules: ["repo_host", "task_tracker", "ci_log", "provider"],
  },
  {
    to: "/settings/runners",
    labelKey: "nav.settingsRunners",
    modules: ["runner"],
  },
  { to: "/settings/sessions", labelKey: "nav.settingsSessions", modules: [] },
  {
    to: "/settings/trust-rules",
    labelKey: "nav.settingsTrustRules",
    modules: ["trust"],
  },
  {
    to: "/settings/agent-roles",
    labelKey: "nav.settingsAgentRoles",
    modules: ["agent_governance"],
  },
  {
    to: "/settings/reviewer-checklist",
    labelKey: "nav.settingsReviewerChecklist",
    modules: ["agent_governance"],
  },
  {
    to: "/settings/tool-allowlist",
    labelKey: "nav.settingsToolAllowlist",
    modules: ["agent_governance"],
  },
  {
    to: "/settings/tool-blacklist",
    labelKey: "nav.settingsToolBlacklist",
    modules: ["agent_governance"],
  },
  {
    to: "/settings/branch-name-template",
    labelKey: "nav.settingsBranchNameTemplate",
    modules: ["settings"],
  },
  {
    to: "/settings/repository-settings",
    labelKey: "nav.settingsRepositorySettings",
    modules: ["settings"],
  },
];

// Разделы после группы «Настройки».
export const BOTTOM_LEVEL: readonly NavItem[] = [
  {
    to: "/self-improvement",
    labelKey: "nav.selfImprovement",
    modules: ["self_learning"],
  },
];

// Маршруты без пункта меню, которым тоже нужен гейт по модулям.
const ROUTES_WITHOUT_MENU_ITEM: readonly NavItem[] = [
  { to: "/tasks/:id", labelKey: "", modules: ["task"] },
];

const MODULES_BY_ROUTE: ReadonlyMap<string, readonly ModuleName[]> = new Map(
  [
    ...TOP_LEVEL,
    ...SETTINGS_ITEMS,
    ...BOTTOM_LEVEL,
    ...ROUTES_WITHOUT_MENU_ITEM,
  ].map((item) => [item.to, item.modules]),
);

// Модули маршрута: единственный источник и для меню, и для гейта
// `RequireModule` в `App.tsx`. Неизвестный путь — ошибка программиста.
export function routeModules(path: string): readonly ModuleName[] {
  const modules = MODULES_BY_ROUTE.get(path);
  if (modules === undefined) {
    throw new Error(`Маршрут ${path} не описан в navigation.ts`);
  }
  return modules;
}

// Раздел ядра, на который ведёт развилка, когда модульных разделов нет.
export const CORE_FALLBACK_PATH = "/settings/sessions";

export function isNavItemVisible(
  item: NavItem,
  isEnabled: (module: ModuleName) => boolean,
): boolean {
  return item.modules.length === 0 || item.modules.some(isEnabled);
}

// Первый по порядку меню модульный раздел, у которого включён хоть один
// модуль; если таких нет — раздел ядра.
export function firstAvailablePath(
  isEnabled: (module: ModuleName) => boolean,
): string {
  const found = [...TOP_LEVEL, ...SETTINGS_ITEMS, ...BOTTOM_LEVEL].find(
    (item) => item.modules.length > 0 && isNavItemVisible(item, isEnabled),
  );
  return found === undefined ? CORE_FALLBACK_PATH : found.to;
}
